import { CategoryInfo, Currency, Language } from './types';

export const PRESET_COLORS = [
  '#10b981', '#3b82f6', '#f43f5e', '#f59e0b', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#6366f1', '#64748b'
];

export const DEFAULT_CATEGORIES: CategoryInfo[] = [
  { 
    name: "Fresh Produce", 
    color: "#10b981", 
    emoji: "🍎", 
    translationKey: "categories.fresh_produce",
    parsingRules: "Always look for weight (lb/kg) or count (ct). Estimate a single unit size if not visible." 
  },
  { 
    name: "Meat & Seafood", 
    color: "#f43f5e", 
    emoji: "🥩", 
    translationKey: "categories.meat_seafood",
    parsingRules: "Primary unit is weight (lb/kg). Extract price-per-pound/kg if visible in the name." 
  },
  { 
    name: "Dairy & Eggs", 
    color: "#3b82f6", 
    emoji: "🥚", 
    translationKey: "categories.dairy_eggs",
    parsingRules: "Focus on volume (oz/ml/l) or count (e.g., 12ct). Note fat content (Whole/2%/Skim)." 
  },
  { 
    name: "Bakery & Bread", 
    color: "#f59e0b", 
    emoji: "🍞", 
    translationKey: "categories.bakery_bread",
    parsingRules: "Extract count or weight. Look for 'Artisan', 'Whole Wheat', or 'Sourdough' descriptors." 
  },
  { 
    name: "Electronics", 
    color: "#8b5cf6", 
    emoji: "💻", 
    translationKey: "categories.electronics",
    parsingRules: "MANDATORY: Extract model numbers, storage capacity (GB/TB), and screen size where applicable." 
  },
  { 
    name: "Apparel", 
    color: "#ec4899", 
    emoji: "👕", 
    translationKey: "categories.apparel",
    parsingRules: "MANDATORY: Extract size (S/M/L/XL or numeric) and color. Identify material if visible." 
  },
  { 
    name: "Household", 
    color: "#64748b", 
    emoji: "🏠", 
    translationKey: "categories.household",
    parsingRules: "Extract count (e.g., 2pk, 4pk) and volume/weight. Note scent or multi-pack details." 
  },
  { 
    name: "Health & Care", 
    color: "#06b6d4", 
    emoji: "🧴", 
    translationKey: "categories.health_care",
    parsingRules: "MANDATORY: Extract volume (oz/ml) and active ingredients or strength (mg) if visible." 
  },
  { 
    name: "Other", 
    color: "#94a3b8", 
    emoji: "📦", 
    translationKey: "categories.other",
    parsingRules: "Provide full brand and variety descriptors following the Product Signature Rule." 
  }
];

export const LANGUAGES: Language[] = [
  { code: 'en', name: 'English', flag: '🇺🇸', defaultCurrency: 'USD' },
  { code: 'es', name: 'Español', flag: '🇪🇸', defaultCurrency: 'EUR' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', defaultCurrency: 'EUR' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', defaultCurrency: 'EUR' },
  { code: 'pt', name: 'Português', flag: '🇵🇹', defaultCurrency: 'BRL' },
  { code: 'ja', name: '日本語', flag: '🇯🇵', defaultCurrency: 'JPY' },
  { code: 'zh', name: '中文', flag: '🇨🇳', defaultCurrency: 'CNY' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', defaultCurrency: 'RUB' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', defaultCurrency: 'AED' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳', defaultCurrency: 'INR' },
  { code: 'sw', name: 'Kiswahili', flag: '🇰🇪', defaultCurrency: 'KES' },
];

export const DEFAULT_LANGUAGE = LANGUAGES[0]; // English
export const DEFAULT_CURRENCY_CODE = 'USD';
export const DEFAULT_COUNTRY = 'Global';
export const DEFAULT_FLAG = '🌐';

export const CURRENCIES: Currency[] = [
  { code: 'USD', label: 'USD ($)', symbol: '$', flag: '🇺🇸', country: 'United States', countryKey: 'countries.us' },
  { code: 'EUR', label: 'EUR (€)', symbol: '€', flag: '🇪🇺', country: 'European Union', countryKey: 'countries.eu' },
  { code: 'GBP', label: 'GBP (£)', symbol: '£', flag: '🇬🇧', country: 'United Kingdom', countryKey: 'countries.uk' },
  { code: 'JPY', label: 'JPY (¥)', symbol: '¥', flag: '🇯🇵', country: 'Japan', countryKey: 'countries.jp' },
  { code: 'CAD', label: 'CAD ($)', symbol: '$', flag: '🇨🇦', country: 'Canada', countryKey: 'countries.ca' },
  { code: 'AUD', label: 'AUD ($)', symbol: '$', flag: '🇦🇺', country: 'Australia', countryKey: 'countries.au' },
  { code: 'CHF', label: 'CHF (Fr)', symbol: 'Fr', flag: '🇨🇭', country: 'Switzerland', countryKey: 'countries.ch' },
  { code: 'CNY', label: 'CNY (¥)', symbol: '¥', flag: '🇨🇳', country: 'China', countryKey: 'countries.cn' },
  { code: 'HKD', label: 'HKD ($)', symbol: '$', flag: '🇭🇰', country: 'Hong Kong', countryKey: 'countries.hk' },
  { code: 'NZD', label: 'NZD ($)', symbol: '$', flag: '🇳🇿', country: 'New Zealand', countryKey: 'countries.nz' },
  { code: 'SEK', label: 'SEK (kr)', symbol: 'kr', flag: '🇸🇪', country: 'Sweden', countryKey: 'countries.se' },
  { code: 'KRW', label: 'KRW (₩)', symbol: '₩', flag: '🇰🇷', country: 'South Korea', countryKey: 'countries.kr' },
  { code: 'SGD', label: 'SGD ($)', symbol: '$', flag: '🇸🇬', country: 'Singapore', countryKey: 'countries.sg' },
  { code: 'NOK', label: 'NOK (kr)', symbol: 'kr', flag: '🇳🇴', country: 'Norway', countryKey: 'countries.no' },
  { code: 'MXN', label: 'MXN ($)', symbol: '$', flag: '🇲🇽', country: 'Mexico', countryKey: 'countries.mx' },
  { code: 'INR', label: 'INR (₹)', symbol: '₹', flag: '🇮🇳', country: 'India', countryKey: 'countries.in' },
  { code: 'BRL', label: 'BRL (R$)', symbol: 'R$', flag: '🇧🇷', country: 'Brazil', countryKey: 'countries.br' },
  { code: 'ZAR', label: 'ZAR (R)', symbol: 'R', flag: '🇿🇦', country: 'South Africa', countryKey: 'countries.za' },
  { code: 'TRY', label: 'TRY (₺)', symbol: '₺', flag: '🇹🇷', country: 'Turkey', countryKey: 'countries.tr' },
  { code: 'AED', label: 'AED (د.إ)', symbol: 'د.إ', flag: '🇦🇪', country: 'United Arab Emirates', countryKey: 'countries.ae' },
  { code: 'RUB', label: 'RUB (₽)', symbol: '₽', flag: '🇷🇺', country: 'Russia', countryKey: 'countries.ru' },
  { code: 'KES', label: 'KES (KSh)', symbol: 'KSh', flag: '🇰🇪', country: 'Kenya', countryKey: 'countries.ke' },
];

/**
 * STRIPE BILLING CONSTANTS
 */
const _isProduction = import.meta.env.VITE_VERCEL_ENV === 'production';
export const STRIPE_STORAGE_ADDON_PRICE_ID = _isProduction
  ? import.meta.env.VITE_STRIPE_STORAGE_ADDON_PRICE_ID
  : (import.meta.env.VITE_STRIPE_SANDBOX_STORAGE_ADDON_PRICE_ID || import.meta.env.VITE_STRIPE_STORAGE_ADDON_PRICE_ID);
export const STRIPE_CREDITS_ADDON_PRICE_ID = _isProduction
  ? import.meta.env.VITE_STRIPE_CREDITS_ADDON_PRICE_ID
  : (import.meta.env.VITE_STRIPE_SANDBOX_CREDITS_ADDON_PRICE_ID || import.meta.env.VITE_STRIPE_CREDITS_ADDON_PRICE_ID);
export const CREDIT_PACK_SIZE = 50;

/**
 * STORAGE QUOTA DEFAULTS (in Bytes)
 */
export const STORAGE_QUOTA_FREE = 50 * 1024 * 1024; // 50 MB
export const STORAGE_QUOTA_PRO = 2 * 1024 * 1024 * 1024; // 2 GB
export const STORAGE_QUOTA_ULTRA = 10 * 1024 * 1024 * 1024; // 10 GB
export const STORAGE_EXPANSION_INCREMENT = 1024 * 1024 * 1024; // 1 GB per purchase

// Intelligence Protocol: Credit Costs
export const CREDIT_COST_BYPASS_TIPS = 1;
export const CREDIT_COST_BYPASS_AUDIT = 2;

export const APP_VERSION = '1.5.0';

export interface PublicPathMetadata {
  path: string;
  translationKey: string;
  isInSiteNavigation?: boolean;
  isAnchor?: boolean;
}

export const PUBLIC_PATHS_METADATA: PublicPathMetadata[] = [
  { path: '/', translationKey: 'nav.home', isInSiteNavigation: true },
  { path: '/#features', translationKey: 'landing.features_title', isInSiteNavigation: true, isAnchor: true },
  { path: '/#how-it-works', translationKey: 'landing.workflow', isInSiteNavigation: true, isAnchor: true },
  { path: '/pricing', translationKey: 'landing.pricing', isInSiteNavigation: true },
  { path: '/features', translationKey: 'features_pages.common.breadcrumb_features', isInSiteNavigation: true },
  { path: '/blog', translationKey: 'nav.blog', isInSiteNavigation: true },
  { path: '/newsroom', translationKey: 'nav.newsroom', isInSiteNavigation: true },
  { path: '/alternatives', translationKey: 'common.comparisons', isInSiteNavigation: true },
  { path: '/faq', translationKey: 'nav.faq', isInSiteNavigation: true },
  { path: '/plu', translationKey: 'common.commodities', isInSiteNavigation: true },
  { path: '/units', translationKey: 'common.units', isInSiteNavigation: true },
  { path: '/deck', translationKey: 'menu.pitch_deck', isInSiteNavigation: true },
  { path: '/about', translationKey: 'nav.about', isInSiteNavigation: true },
  { path: '/brand', translationKey: 'menu.branding', isInSiteNavigation: true },
  { path: '/architect', translationKey: 'admin.architects', isInSiteNavigation: true },
  { path: '/partners', translationKey: 'admin.partners', isInSiteNavigation: true },
  { path: '/privacy', translationKey: 'menu.privacy', isInSiteNavigation: true },
  { path: '/terms', translationKey: 'menu.terms', isInSiteNavigation: true },
  { path: '/sign-in', translationKey: 'auth.sign_in', isInSiteNavigation: false },
  { path: '/sign-up', translationKey: 'auth.create_account', isInSiteNavigation: false }
];

export const PUBLIC_PATHS = PUBLIC_PATHS_METADATA.filter(m => !m.isAnchor).map(m => m.path);

